import { useApolloClient } from '@apollo/client';
import { useCallback, useContext } from 'react';
import {
  ConversationList_GetConversationsDocument,
  ConversationList_GetConversationsQuery,
  ConversationList_GetConversationsQueryVariables,
  Folder,
  ReadRepliedStatus,
  RenterProgressStatus,
} from '../types/GraphqlTypes';
import { InboxPageContext } from '../contexts/InboxPageContextProvider';
import { MAX_OLD_CONVERSATION_DAYS } from '../constants/ConfigConstants';

type Conversations = ConversationList_GetConversationsQuery['ZRM_getLatestConversationsV2']['conversations'];

export type ClearConversationListQueryCacheArgs = {
  daysBack?: number;
  folder?: Folder;
  listingAlias?: string | null;
  readRepliedStatus?: ReadRepliedStatus;
  renterProgressStatus?: RenterProgressStatus;
};

export default () => {
  const apolloClient = useApolloClient();
  const { clientTimeZone, daysBack, folder, isByProperty, listingAlias, readRepliedStatus, renterProgressStatus } =
    useContext(InboxPageContext);

  /**
   * @param conversationId of the conversation to remove from ConversationList
   * @param folderToRemoveConversationFrom defaults to existing folder the user is in
   * @returns existing cached list and updated filtered list of conversations
   * @description Specific actions require a conversation item from being removed from ConversationList
   *      This function allows you to pass in a conversationId of the conversation to remove, and returns the
   *      new list of conversations that's written to the cache
   */
  const removeConversationFromList = useCallback(
    (
      conversationId?: string,
      folderToRemoveConversationFrom?: Folder,
    ): { conversations: Conversations; updatedConversations: Conversations } => {
      const cachedConversationListData = apolloClient.cache.readQuery<
        ConversationList_GetConversationsQuery,
        ConversationList_GetConversationsQueryVariables
      >({
        query: ConversationList_GetConversationsDocument,
        variables: {
          clientTimeZone,
          daysBack: daysBack ? MAX_OLD_CONVERSATION_DAYS : null,
          folder,
          listingAlias: isByProperty ? listingAlias : null,
          readRepliedStatus,
          renterProgressStatus,
        },
      });

      const conversations = cachedConversationListData?.ZRM_getLatestConversationsV2.conversations || [];
      const updatedConversations = conversations.filter(
        (conversation) => conversation.conversationId !== conversationId,
      );

      apolloClient.cache.writeQuery({
        query: ConversationList_GetConversationsDocument,
        variables: {
          clientTimeZone,
          daysBack: daysBack ? MAX_OLD_CONVERSATION_DAYS : null,
          folder: folderToRemoveConversationFrom || folder,
          listingAlias: isByProperty ? listingAlias : null,
          readRepliedStatus,
          renterProgressStatus,
          // Custom flag used in ZRM_getLatestConversationsV2 apollo cache merge function
          skipMerge: true,
        },
        data: {
          ZRM_getLatestConversationsV2: {
            ...cachedConversationListData?.ZRM_getLatestConversationsV2,
            conversations: updatedConversations,
          },
        },
      });

      return {
        conversations,
        updatedConversations,
      };
    },
    [clientTimeZone, folder, isByProperty, listingAlias, readRepliedStatus, renterProgressStatus],
  );

  /**
   * @param currentConversationId conversationId of the conversation to locate in list of conversations
   * @param conversations list of conversations from ConversationList_GetConversationsQuery
   * @returns nextConversationId and nextListingAlias
   * @description Specific actions such as archive/unarchive require a conversation item from being removed from ConversationList
   *      This function allows you to pass in a conversationId (for example, the conversationId of an archived conversation) in order
   *      to find and redirect to the next conversation in the list in the UI
   */
  const getConversationToRedirectToNext = useCallback(
    (
      currentConversationId: string,
      conversations: ConversationList_GetConversationsQuery['ZRM_getLatestConversationsV2']['conversations'],
    ) => {
      let nextConversationId = '';
      let nextListingAlias = '';

      if (conversations.length > 1) {
        const currentConversationIndex = conversations.findIndex(
          (conversation) => conversation.conversationId === currentConversationId,
        );

        if (currentConversationIndex !== -1) {
          const nextConversationIndex = (currentConversationIndex + 1) % conversations.length;
          nextConversationId = conversations[nextConversationIndex].conversationId;
          nextListingAlias = conversations[nextConversationIndex].listingAlias;
        } else {
          nextConversationId = conversations[0].conversationId;
          nextListingAlias = conversations[0].listingAlias;
        }
      }

      return {
        nextConversationId,
        nextListingAlias,
      };
    },
    [],
  );

  const clearGetLatestConversationsQueryCache = useCallback(
    ({
      daysBack,
      folder,
      listingAlias,
      readRepliedStatus,
      renterProgressStatus,
    }: ClearConversationListQueryCacheArgs) => {
      apolloClient.cache.evict({
        fieldName: 'ZRM_getLatestConversationsV2',
        args: {
          daysBack: daysBack || null,
          clientTimeZone,
          folder,
          listingAlias: listingAlias || null,
          readRepliedStatus,
          renterProgressStatus,
        },
      });
      apolloClient.cache.gc();
    },
    [],
  );

  return {
    clearGetLatestConversationsQueryCache,
    getConversationToRedirectToNext,
    removeConversationFromList,
  };
};
